import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  CalendarCheck,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDashboard } from "../Dashboard/Hooks/useDashboard";
import toast from "react-hot-toast";
import { formatAmountINR } from "../Dashboard/Constants";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { ATTENDANCE_TYPES } from "../Attendance/constants";

const Checklist = () => {
  const [data, setData] = useState({
    bills: { total_amount: 0 },
    attendance: { count: 0, results: [] },
  });
  const { fetchChecklist } = useDashboard();
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchData = async () => {
    try {
      const res = await fetchChecklist();
      setData(res);
    } catch (err) {
      toast.error("Failed to load checklist");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const attendanceRecords = data?.attendance?.results;

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? attendanceRecords?.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === attendanceRecords?.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="rounded-2xl shadow-lg bg-white px-4 pt-3 pb-4">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-3 border-b pb-2">
          <FileText className="w-5 h-5" /> Checklist KPI
        </h2>

        {/* KPI Section */}
        <div className="flex flex-col gap-4 h-[270px]">
          {/* Total Bills */}
          <div className="flex-1 flex items-center gap-4 rounded-2xl px-2 bg-gradient-to-r from-blue-50 to-blue-100 shadow-md hover:shadow-xl transition-shadow">
            <DollarSign className="w-7 h-7 text-blue-700" />
            <div>
              <div className="text-gray-500 text-sm font-medium">
                Total Bills Amount
              </div>
              <div className="text-2xl font-bold text-blue-800 mt-1">
                {formatAmountINR(data?.bills?.total_amount || 0)}
              </div>
            </div>
          </div>

          {/* Attendance Slider */}
          <div className="flex-1 relative flex items-start gap-4 p-4 rounded-2xl bg-gradient-to-r from-green-50 to-green-100 shadow-md hover:shadow-xl transition-shadow overflow-hidden">
            <div className="flex-1 relative">
              <div className="flex items-center gap-2">
                <CalendarCheck className="w-6 h-6 text-green-700 mt-1" />
                <div className="text-gray-500 text-sm font-medium">
                  Today's Attendance
                </div>
              </div>

              <div className="relative h-[100px]">
                <AnimatePresence mode="wait">
                  {attendanceRecords?.length > 0 ? (
                    <motion.div
                      key={attendanceRecords[currentIndex]?.id || currentIndex}
                      initial={{ x: 100, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -100, opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute w-full flex justify-center items-center"
                    >
                      <div className="text-gray-700 mt-1 space-y-2 w-full">
                        <div className="font-semibold text-sm truncate max-w-full">
                          {attendanceRecords[currentIndex]?.site}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {[
                            ATTENDANCE_TYPES.CHECKIN,
                            ATTENDANCE_TYPES.CHECKOUT,
                          ].map((type) => {
                            const event = attendanceRecords[
                              currentIndex
                            ]?.events.find(
                              (e) => e.type.toLowerCase() === type
                            );

                            // Only show if event exists or explicitly show Pending
                            return (
                              <span
                                key={type}
                                className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs font-medium capitalize"
                              >
                                {type}:{" "}
                                {event
                                  ? formatTimeAMPM(event.time)?.toUpperCase()
                                  : "Pending"}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="no-attendance"
                      initial={{ x: 100, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -100, opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute w-full text-gray-500 text-sm"
                    >
                      <div className="w-full h-max flex justify-center items-center text-gray-300 flex-col mt-2">
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                            Checkin : Pending
                          </span>
                          <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                            Checkout : Pending
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Indicator dots */}
              {attendanceRecords?.length > 1 && (
                <div className="flex justify-center gap-2 mt-3">
                  {attendanceRecords?.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? "bg-green-600" : "bg-green-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Arrows */}
            {attendanceRecords?.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                >
                  <ChevronLeft className="w-5 h-5 text-green-700" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                >
                  <ChevronRight className="w-5 h-5 text-green-700" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Checklist;
